/**
 * v-tooltip 디렉티브 (tippy.js 6)
 *
 * 사용법:
 * 1) title 폴백 — 아이콘 버튼 권장 방식
 *    <button v-tooltip class="p-icon-btn" title="수정">…</button>
 *    <button v-tooltip class="p-icon-btn" :title="$t('label.101')">…</button>
 *    title 을 읽어 툴팁으로 띄우고, 접근 이름을 aria-label 로 옮긴 뒤
 *    title 속성은 제거합니다(브라우저 기본 회색 툴팁과 중복 방지).
 *    :title 이 바뀌면 updated 훅에서 다시 읽어 갱신합니다.
 *
 * 2) 값 지정
 *    <input v-tooltip="someText" />
 *    <input v-tooltip.html="htmlText" />   ← HTML 을 넣으려면 .html 필수
 *
 * 값도 title 도 없으면 인스턴스를 만들지 않습니다.
 */

import tippy from 'tippy.js';

const KEY = '__pTooltip';

// z-index 단일 소스는 SCSS 의 $p-z-popover 입니다.
// JS 에서 SCSS 변수를 읽을 수 없고, tippy 는 popper 루트에 z-index 를
// 인라인으로 쓰기 때문에 CSS 로도 덮을 수 없습니다.
// 그래서 _pineapple-tooltip.scss 가 :root 에 --p-z-popover 로 내보낸 값을 읽습니다.
let zIndexCache = null;
function popoverZIndex() {
  if (zIndexCache !== null) return zIndexCache;
  let n = NaN;
  try {
    n = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--p-z-popover'), 10);
  } catch (e) {
    // 브라우저가 아닌 환경
  }
  zIndexCache = Number.isNaN(n) ? 13000 : n; // fallback = $p-z-popover
  return zIndexCache;
}

const BASE_OPTIONS = {
  theme: 'pineapple',
  placement: 'top',
  animation: 'fade',
  arrow: true,
  offset: [0, 8],
  delay: [250, 0], // 표 위를 스칠 때 우수수 뜨지 않도록
  duration: [120, 80],
  maxWidth: 260,
  touch: 'hold', // 터치는 롱프레스에서만. 탭은 버튼 동작 그대로
  hideOnClick: true,
  appendTo: () => document.body, // 조상 overflow 클리핑 회피
};

function createState() {
  return { instance: null, text: '', ownsAria: false, html: false };
}

function sync(el, binding) {
  const state = el[KEY];
  // 빈 문자열은 "툴팁 없음"이라는 명시적 표현으로 봅니다(값 모드 유지 → 인스턴스 제거).
  // 바인딩 자체가 없을 때(bare v-tooltip)만 title 폴백으로 넘어갑니다.
  const explicit = binding.value !== undefined && binding.value !== null;

  let text;
  if (explicit) {
    text = String(binding.value);
  } else {
    const title = el.getAttribute('title');
    if (title) {
      el.removeAttribute('title');
      // 아이콘 버튼은 접근 이름이 없습니다(<i> 안의 리거처 이름이 그대로 읽힙니다).
      // title 을 aria-label 로 승격시킵니다. 작성자가 직접 쓴 aria-label 은 건드리지 않습니다.
      if (!el.hasAttribute('aria-label') || state.ownsAria) {
        el.setAttribute('aria-label', title);
        state.ownsAria = true;
      }
      text = title;
    } else {
      // 정적 title 은 첫 렌더에서 제거되고 Vue 가 되돌려놓지 않습니다.
      // 속성이 없다고 툴팁을 지우면 안 되고, 마지막 값을 유지해야 합니다.
      text = state.text;
    }
  }

  if (state.instance && text === state.text) return;
  state.text = text;

  if (!text) {
    if (state.instance) {
      state.instance.destroy();
      state.instance = null;
    }
    return;
  }

  const options = {
    ...BASE_OPTIONS,
    content: text,
    zIndex: popoverZIndex(),
    allowHTML: state.html,
    // aria-label 을 우리가 심었으면 tippy 의 aria-describedby 까지 붙는 순간
    // 스크린리더가 같은 문장을 두 번 읽습니다.
    // 값 지정 방식(기존 호출부)은 aria-label 을 만들지 않으므로 tippy 기본 동작을 유지합니다.
    ...(state.ownsAria ? { aria: { content: null, expanded: false } } : {}),
  };

  if (state.instance) state.instance.setProps(options);
  else state.instance = tippy(el, options);
}

export default {
  mounted(el, binding) {
    el[KEY] = createState();
    el[KEY].html = !!binding.modifiers.html;
    sync(el, binding);
  },

  updated(el, binding) {
    if (!el[KEY]) {
      el[KEY] = createState();
      el[KEY].html = !!binding.modifiers.html;
    }
    sync(el, binding);
  },

  // 모달·슬라이드 패널이 사라지는 트랜지션 동안 툴팁이 남는 것을 줄입니다.
  beforeUnmount(el) {
    const state = el[KEY];
    if (state && state.instance) state.instance.hide();
  },

  // v-for 행이 다시 그려질 때 인스턴스가 쌓이는 것을 막습니다.
  unmounted(el) {
    const state = el[KEY];
    if (!state) return;
    if (state.instance) state.instance.destroy();
    delete el[KEY];
  },
};
